import { Directive, OnInit, inject } from '@angular/core';
import { IColumn, IHttpParams, ISortParams } from '../interfaces';
import { ParamsHandler } from './http-params.handler';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { ESortOrder } from '../enums';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

export interface ITableQueryParams extends IHttpParams, ISortParams {}

@UntilDestroy()
@Directive()
/**
 * An abstract class representing a table component.
 * @abstract
 */
export abstract class TableComponent implements OnInit {
  public pageSizeOptions = [10, 20, 30];
  public columns!: IColumn[];
  public paramsHandler = new ParamsHandler<ITableQueryParams>({
    pageNumber: 1,
    pageSize: this.pageSizeOptions[0],
    fromSmallerToLarger: true,
  });

  get paginationParams(): IHttpParams {
    return this.paramsHandler.params;
  }

  protected _route = inject(ActivatedRoute);
  protected _router = inject(Router);

  protected abstract _getTableId(): string;

  protected abstract _getStoredTableColumns(): Observable<IColumn[]>;

  public ngOnInit(): void {
    this._loadColumns();

    this.paramsHandler.params$.pipe(untilDestroyed(this)).subscribe({
      next: params => {
        this._handleParamsChanged(params);
      },
    });
  }

  /**
   * Loads the table columns from storage and handles the loaded columns.
   * @protected
   * @returns {void}
   */
  protected _loadColumns(): void {
    this._getStoredTableColumns().subscribe({
      next: columns => {
        this._handleColumnsLoaded(columns);
      },
    });
  }

  /**
   * Handles the loaded columns by setting the `columns` property to the provided columns.
   * @param columns - The columns to be set.
   */
  protected _handleColumnsLoaded(columns: IColumn[]): void {
    this.columns = columns;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected _handleParamsChanged(params: ITableQueryParams): void {}

  /**
   * Handles changes to the query parameters of an NZ table.
   * @param params - The new query parameters.
   */
  public onNzTableQueryParamsChange(params: NzTableQueryParams): void {
    const sort = params.sort.find(i => i.value != null);
    const descending = sort?.value
      ? sort.value === 'descend'
        ? ESortOrder.DESCENDING
        : ESortOrder.ASCENDING
      : null;

    this.paramsHandler.patchParams({
      pageNumber: params.pageIndex,
      pageSize: params.pageSize,
      orderByField: sort?.key,
      orderBy: descending,
    });
  }
}
